import { useState, useEffect } from "react";
import { AutoComplete, AutoCompleteChangeEvent } from "primereact/autocomplete";
import { FieldType } from "../../api/types/field-type";
import { useDebounce } from "../../common/useDebounce";
import { useFieldTypeSearchGroups } from "../../api/queries/field-types/useFieldTypeSearchGroups";

interface AutoCompleteFieldTypeProps {
  value?: FieldType | null;
  onChange: (fieldType: FieldType | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export default function AutoCompleteFieldType({
  value,
  onChange,
  placeholder = "Search parent field type...",
  disabled = false,
}: AutoCompleteFieldTypeProps) {
  const [suggestions, setSuggestions] = useState<FieldType[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const searchQueryDebounced = useDebounce(searchQuery, 500);

  const { data: fieldTypeSearchResponse } = useFieldTypeSearchGroups({
    q: searchQueryDebounced,
  });

  useEffect(() => {
    if (fieldTypeSearchResponse) {
      setSuggestions(fieldTypeSearchResponse.data || []);
    } else {
      setSuggestions([]);
    }
  }, [fieldTypeSearchResponse]);

  const handleChange = (event: AutoCompleteChangeEvent) => {
    if (typeof event.value === "string") {
      setSearchQuery(event.value);
      onChange(null);
    } else {
      const fieldType: FieldType = event.value;
      setSearchQuery(fieldType.name);
      onChange(fieldType);
    }
  };

  const handleCompleteMethod = () => {
    // Empty method - search is handled by debounced effect
  };

  const itemTemplate = (item: FieldType) => {
    return (
      <div className="flex align-items-center">
        <div>
          <div className="font-medium">{item.name}</div>
          <div className="text-sm text-500">{item.type}</div>
        </div>
      </div>
    );
  };

  return (
    <AutoComplete
      value={value?.name || searchQuery}
      suggestions={suggestions}
      completeMethod={handleCompleteMethod}
      onChange={handleChange}
      field="name"
      placeholder={placeholder}
      disabled={disabled}
      itemTemplate={itemTemplate}
      emptyMessage="No field types found"
      className="w-full"
    />
  );
}
